import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from '../RouterCompatibility';
import moment from 'moment';
import CollapsibleMultiselect from '../Multiselects/CollapsibleMultiselect';
import DropdownList from 'react-widgets/DropdownList';
import Custom from '../Multiselects/Custom';
import DatePicker from './DataComponent__DatePicker';
import DynamicSelectSegment from '../DynamicSelectSegment';
import ButtonsList from '../ButtonsList';
import InputGroupMultiselect from '../InputGroupMultiselect';
import CustomNotify from '../Notify';
import debounce from '../../helpers/debounce';
import query from '../../helpers/query';
import runtimeConfig from '../../config/runtimeConfig';
import UserStore from '../../stores/UserStore';
import TZ from '../../utils/Timezone/Timezone.js';

const initPage = runtimeConfig.initialPage;
const minLength = 3;
const getUserTimezone = () => UserStore.getValidTimeZone();
const getNowInUserTimezone = () => moment().tz(getUserTimezone());
const templateKeys = {
    datepicker: 'getDatepicker',
    select: 'getSelect',
    multiselect: 'getMultiselect',
    dropdown: 'getDropdown',
    textInput: 'getTextInput',
    custom: 'getCustom',
    dynamic: 'getDynamic',
    goalTree: 'getSelectGoalTree',
    currency: 'getNull'
};

class DataComponentFilterFinanceBlock extends React.Component {
    constructor(props, context) {
        super();

        const parsedQuery = query.parse(props.location && props.location.query);
        let values = {
            page: parsedQuery.page || initPage,
            ...this.getDefValues(props.fields),
            ...parsedQuery
        };

        if (props.location && props.location.pathname && "/office/finances/report" !== props.location.pathname) {
            values.createStartDate = parsedQuery.createStartDate || parsedQuery.startDate || props.startDate || moment().subtract(6, 'days');
            values.createEndDate = parsedQuery.createEndDate || parsedQuery.endDate || props.endDate || moment().endOf('day');
            values.statuses = 'paid';
            values.sources = ['conversion'];
        }

        (props.fields || []).forEach(field => {
            if (field && field.name === 'datepicker' && field.withTimezone) {
                const startDateKey = `${field.key}StartDate`;
                const isSelected = field.disabled || values[startDateKey];
                if (!isSelected) return;

                const timezoneKey = `${field.key}Timezone`;
                if (!values[timezoneKey]) {
                    values[timezoneKey] = getUserTimezone();
                }
            }
        });

        this.state = {
            selected: this.getSelected(props.fields, values),
            values
        };

        this.staticNames = context.t && context.t.dataTable || {};
        this.staticNamesActions = context.t && context.t.actions || {};

        this.messagesForSelect = {
            emptyList: this.staticNames.emptyList
        };

        this.timezoneOptions = (TZ.getAllTimezones() || []).map(tz => ({
            value: tz.key,
            title: tz.nameLong || tz.name || tz.key
        }));

        this.onAction = this.onAction.bind(this);
        this.onDataPickerApply = this.onDataPickerApply.bind(this);
        this.onSelectChange = this.onSelectChange.bind(this);
        this.onTextInputChange = this.onTextInputChange.bind(this);
        this.onAddFilter = this.onAddFilter.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.textInputChange = debounce(this.textInputChange.bind(this), 500);
    }

    componentDidMount() {
        this.onAction('firstRender');
    }

    getDefValues(fields = []) {
        let values = {};

        (fields || []).map(item => {
            if (!item.value) return;

            if (item.name === 'datepicker') {
                item.value.startDate && (values[`${item.key}StartDate`] = item.value.startDate);
                item.value.endDate && (values[`${item.key}EndDate`] = item.value.endDate);
                item.value.timezone && (values[`${item.key}Timezone`] = item.value.timezone);
            } else {
                values[item.key] = item.value;
            }
        });

        return values;
    }

    getSelected(fields = [], values = {}) {
        let selected = [];

        (fields || []).map((item, i) => {
            if (item.name === 'datepicker' && values[`${item.key}StartDate`] || values[item.key]) {
                selected.push({
                    ...item,
                    fieldIndex: i
                });
            }
        });

        return selected;
    }

    getValue(data) {
        if (data.name === 'datepicker') {
            return {
                startDate: this.state.values[`${data.key}StartDate`] || moment().subtract(6, 'days'),
                endDate: this.state.values[`${data.key}EndDate`] || moment().endOf('day'),
                timezone: this.state.values[`${data.key}Timezone`] || getUserTimezone()
            };
        } else if (data.name === 'textInput') {
            return this.state.values[data.key] || '';
        } else if (data.name === 'multiselect') {
            const v = this.state.values[data.key];
            if (!v) return [];
            return Array.isArray(v) ? v : [v];
        }

        return this.state.values[data.key];
    }

    getNull = () => {
        return null;
    }

    textInputChange(name) {
        if (this.state.values[name] && this.state.values[name].length > 0 && this.state.values[name].length <= minLength) {
            let message = (this.context.t && this.context.t.validate && this.context.t.validate.minLineLength) ? this.context.t.validate.minLineLength.replace(/%s/i, minLength) : '';
            CustomNotify(message, 'error');
        }
    }

    deleteValue(values, item) {
        if (item.name === 'datepicker') {
            delete values[`${item.key}StartDate`];
            delete values[`${item.key}EndDate`];
            delete values[`${item.key}Timezone`];
        } else {
            delete values[item.key];
        }
    }

    onDataPickerApply(datepicker, key) {
        const newState = {
            values: {
                ...this.state.values,
                [`${key}StartDate`]: datepicker.startDate.clone().startOf('day'),
                [`${key}EndDate`]: datepicker.endDate.clone().endOf('day')
            }
        };
        if(datepicker.timezone) {
            newState.values[`${key}Timezone`] = datepicker.timezone
        }
        this.setState(newState, () => this.onAction());
    }

    onTextInputChange(event) {
        const name = event.target.name;

        this.setState({
            values: {
                ...this.state.values,
                [name]: event.target.value
            }
        }, () => {
            this.textInputChange(name);
        });
    }

    onSelectChange(event) {
        const name = event.target.name;

        this.setState({
            values: {
                ...this.state.values,
                [name]: event.target.value
            }
        });
    }

    onChange = (value, key) => {
        this.setState({
            values: {
                ...this.state.values,
                [key]: value
            }
        }, () => this.onAction());
    }

    onChangeDynamic(value, key) {
        const newValue = (value || []).map(item => {return {title: item.title, value: item.value}});

        this.setState({
            values: {
                ...this.state.values,
                [key]: newValue
            }
        });
    }

    onAction(type) {
        this.setState({
            values: {
                ...this.state.values,
                page: initPage
            }
        }, () => {
            this.props.onAction(this.state.values, type);
        });
    }

    onChangeFilter(item, index) {
        const fieldIndex = this.props.fields.findIndex(data => data.key === item.key);
        let selected = [...this.state.selected];
        let values = {...this.state.values};
        let oldItem = selected[index];

        this.deleteValue(values, oldItem);
        selected[index] = {
            ...item,
            fieldIndex
        };

        this.setState({
            selected,
            values
        });
    }

    onAddFilter(item) {
        const fieldIndex = this.props.fields.findIndex(data => data.key === item.key);
        let selected = [...this.state.selected];

        selected.push({
            ...item,
            fieldIndex
        });

        this.setState({
            selected
        });
    }

    onDelete(item, index) {
        let selected = [...this.state.selected];
        let values = {...this.state.values};
        let deletedItem = selected.find( ({ name }) => name === item.name );

        index = index === undefined ? selected.indexOf(deletedItem) : index;

        this.deleteValue(values, deletedItem);
        selected.splice(index, 1);

        this.setState({
            selected,
            values
        });
    }

    getDatepicker(data) {
        const onDataPickerApply = (item) => this.onDataPickerApply(item, data.key);
        return (
            <React.Fragment>
                <DatePicker
                    onApply={onDataPickerApply}
                    timezone={this.getValue(data).timezone}
                    className="btn form-control reactdaypicker-block"
                    {...this.getValue(data)}
                />
                {data.withTimezone ? (
                    this.getTimezoneDropdown(data)
                ) : null}
            </React.Fragment>
        );
    }

    getTimezoneDropdown(data) {
        return (
            <DropdownList
                messages={this.messagesForSelect}
                className={'rw-dropdownlist-select'}
                dataKey='value'
                textField='title'
                placeholder={data.placeholder || ''}
                data={this.timezoneOptions}
                value={this.getValue({ ...data }).timezone}
                onChange={(item) => {
                    // this.onChange(item && item.value, `${data.key}Timezone`);

                    const currDt = this.getValue({ ...data });
                    const startDateNew = moment.tz({
                        year: currDt.startDate.year(),
                        month: currDt.startDate.month(),
                        date: currDt.startDate.date(),
                    }, item.value).startOf('day');
                    const endDateNew = moment.tz({
                        year: currDt.endDate.year(),
                        month: currDt.endDate.month(),
                        date: currDt.endDate.date(),
                    }, item.value).endOf('day');
                    const dtItem = {
                        startDate: startDateNew,
                        endDate: endDateNew,
                        timezone: item.value,
                    }
                    this.onDataPickerApply(dtItem, data.key);
                }}
                filter='contains'
            />
        );
    }

    getSelect(select) {
        return (
            <select
                onChange={this.onSelectChange}
                className="form-control long-input right"
                name={select.key}
                value={this.getValue(select)}
            >
                {(this.props.fields[select.fieldIndex]?.data || []).map((option, i) => (
                    <option key={i} value={option.value}>{option.title}</option>
                ))}
            </select>
        );
    }

    getDropdown(dropdown) {
        return (
            <DropdownList
                messages={this.messagesForSelect}
                className={'rw-dropdownlist-select'}
                dataKey='value'
                textField='title'
                placeholder={dropdown.placeholder || ''}
                data={this.props.fields[dropdown.fieldIndex]?.data || []}
                value={this.getValue(dropdown)}
                onChange={(value) => { this.onChange(value, dropdown.key); }}
            />
        );
    }

    getMultiselect(select) {
        return (
            <CollapsibleMultiselect
                messages={this.messagesForSelect}
                placeholder={select.placeholder || ''}
                className='right'
                dataKey='value'
                textField='title'
                onChange={(value) => { this.onChange(value, select.key); }}
                data={this.props.fields[select.fieldIndex]?.data || []}
                name={select.key}
                value={this.getValue(select)}
            />
        );
    }

    getTextInput(data) {
        return (
            <input
                className="form-control"
                type="text"
                name={data.key}
                onChange={this.onTextInputChange}
                placeholder={data.placeholder}
                value={this.getValue(data)}
            />
        );
    }

    getCustom(data) {
        const onChangeCustom = (item) => this.onChange(item, data.key);

        return (
            <Custom
                segment={data.label}
                values={this.getValue(data)}
                onAction={onChangeCustom}
            />
        );
    }

    getDynamic(data) {
        const onChangeDynamic = (item) => this.onChangeDynamic(item, data.key);

        return (
            <DynamicSelectSegment
                segment={data.segment}
                multiselect={true}
                values={this.getValue(data)}
                onAction={onChangeDynamic}
            />
        );
    }

    getSelectGoalTree(data) {
        const onChange = (item) => this.onChange(item, data.key);

        return (
            <InputGroupMultiselect
                nesting={data.nesting}
                onChange={onChange}
                tags={this.getValue(data)}
            />
        );
    }

    getChangeFunction = (value, key, name) => {
        if(name === 'currency' ||  key === 'currency') {
            return this.onChange(value, key)
        } else if(name === 'datepicker') {
            return this.onDataPickerApply(value, key)
        } else {
            alert('No function assigned')
        }
    }

    renderFilter = (item = {}, i) => {
        const onChangeFilter = (item) => this.onChangeFilter(item, i);
        const onChangeDropdown = (i >= 0) ? onChangeFilter : this.onAddFilter;
        const onDelete = () => this.onDelete(item, i);

        return (
            <div key={item.key}>
                {item.name
                    ? (
                        <React.Fragment>
                            <div className="table-filter__item">
                                {item.label && <label> {item.label}
                                    {!["datepicker", "custom", "multiselect", "dynamic", "goalTree"].includes(item.name) ? (<i className="fa fa-search"></i>) : null}
                                </label>}
                                {this[templateKeys[item.name]](item)}
                            </div>
                        </React.Fragment>
                    )
                    : null
                }
                {item.withList 
                    ? <ButtonsList 
                        fields={this.props.fields} 
                        data={item} 
                        onChange={(value, key) => this.getChangeFunction(value, key, item.name)} 
                        currentValue={this.state.values[item.key]} 
                        showSubtitle={item.showSubtitle}
                       />
                    : null
                }
            </div>
        );
    }

    render() {
        if (!this.props.fields.length) return null;

        return (
            <div className="finance-filter">
                <div className="table-filter">
                    {this.state.selected.map( filter => this.renderFilter(filter))}
                    {this.props.fields.length !== this.state.selected.length
                        ? this.renderFilter()
                        : null
                    }
                </div>
            </div>
        );
    }
};

DataComponentFilterFinanceBlock.propTypes = {
    fields: PropTypes.array
};

DataComponentFilterFinanceBlock.defaultProps = {
    fields: []
};

DataComponentFilterFinanceBlock.contextTypes = {
    t: PropTypes.object
};

export default withRouter(DataComponentFilterFinanceBlock);
